import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, RefreshCw, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const Row = ({ ok, label, detail }) => (
  <div className="flex items-start gap-3 py-3 border-b border-border last:border-0" data-testid={`payments-check-${ok ? "ok" : "fail"}`}>
    {ok ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
        : <XCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />}
    <div>
      <p className="text-sm font-medium text-foreground">{label}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{detail}</p>
    </div>
  </div>
);

export default function PaymentsHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stripe-readiness");
      setData(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not run the check");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const live = data?.key_mode === "live";
  const ready = data?.ready;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="payments-health-panel">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /> Payments Health Check</h2>
          <p className="text-sm text-muted-foreground mt-1">A plain-English check of everything Stripe needs to accept payments and pay out shops.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="payments-health-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Re-check</span>
        </Button>
      </div>

      {loading && !data && (
        <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Running checks…</div>
      )}

      {data && (
        <>
          <div className={`rounded-xl border p-4 ${ready ? "border-emerald-500/40 bg-emerald-500/10" : "border-amber-500/40 bg-amber-500/10"}`} data-testid="payments-health-summary">
            <div className="flex items-start gap-3">
              {ready ? <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0" /> : <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0" />}
              <div>
                <p className="font-semibold text-foreground">
                  {ready ? "You're all set for live payments." : "A few things still need attention."}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {ready
                    ? "Every check below passed and you're using a live Stripe key. Real payments and shop payouts will work."
                    : "Fix the items marked with a red ✗ below. Each one explains exactly what's missing."}
                </p>
              </div>
            </div>
          </div>

          <div className={`rounded-xl border p-4 ${live ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/10"}`} data-testid="payments-mode-banner">
            <div className="flex items-center gap-3">
              {live ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <XCircle className="w-5 h-5 text-red-400" />}
              <div>
                <p className="text-sm font-medium text-foreground">
                  {live ? "Using a LIVE Stripe key — real money will move."
                        : data.key_mode === "test" ? "Using a TEST Stripe key — no real money will move."
                        : "Stripe key type could not be determined."}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Mode flag: {data.mode_flag || "not set"} · Account: {data.account_id || "unknown"}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            {data.checks?.map((c) => <Row key={c.key} ok={c.ok} label={c.label} detail={c.detail} />)}
          </div>
        </>
      )}
    </div>
  );
}
